//
// Copyright ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import { useFetchOrganizationDeployments } from "../../newDeployment/useFetchOrganizationDeployments";

// Resolves deployment IDs to names for chart legends.
//
// GetCreditUsage returns deployment IDs only. An organization's usage history
// includes deployments that no longer exist, and their usage is real and was
// invoiced, so it must stay in the totals. A deployment that cannot be resolved
// — deleted, or no longer visible to the caller — falls back to its raw ID
// rather than being dropped, so the series still renders and the credits still
// add up to the shown total.
//
// Resolution failure must never fail the page: on error every ID falls back.
export const useResolveDeploymentNames = (deploymentIds: string[]) => {
  // Goes through the shared useFetchOrganizationDeployments hook - the exact
  // same pageNumber/pageSize DeploymentUsageFilter already requests with -
  // rather than issuing its own ListDeploymentsByFilter call under a
  // different react-query key. On OrganizationUsageView both hooks are
  // mounted together (DeploymentUsageFilter unconditionally, this one
  // whenever the chart is stacked by deployment), so a distinct key meant
  // the organization's full deployment list was fetched twice; sharing the
  // key lets react-query serve both from the one request/cache entry.
  //
  // enabled: deploymentIds.length > 0 - still needed even though the query
  // itself is shared: DeploymentUsageView (single-deployment page, no
  // DeploymentUsageFilter mounted) also calls this hook via CreditUsageChart,
  // always with an empty deploymentIds (that page is never stacked by
  // deployment) - without this gate it would start fetching the org's whole
  // deployment list for no reason on every page load there.
  const { data } = useFetchOrganizationDeployments({ pageNumber: 0, pageSize: 500, enabled: deploymentIds.length > 0 });

  const namesById = new Map<string, string>();
  (data?.items || []).forEach((d) => {
    if (d.id && d.name) {
      namesById.set(d.id, d.name);
    }
  });

  return {
    nameFor: (id: string): string => namesById.get(id) || id,
  };
};
